import { toast } from "@/components"
import { useEffect, useState } from "react"
import { StripePriceWithProduct, StripeProductWithMetadata } from "../../../../app/api/subscription/prices/route"
import { attachStripePriceDataToPlans } from "./useAttachStripePricingDataToPlans"
import { ampli } from "@/ampli"
import { useQuery } from "@apollo/client"
import { UserEntitledToFreeTrialDocument } from "@/gql/graphql"
import { graphql } from "@/gql"
import { CheckoutModalEntryPoint } from "./SubscriptionCheckoutModal"

graphql(`
  query UserEntitledToFreeTrial {
    currentUser {
      id
      creative {
        id
        proMembership {
          id
          eligibleForIntroductoryOffer
        }
      }
    }
  }
`)

export type DefaultProduct = {
  type: "subscription" | "one-time-purchase"
  name: string
  caption: string
  stepTwoDescription: string
  productType: "yearly" | "monthly" | "daily"
}

type ProductType = "monthly" | "yearly" | "daily"

const subscriptionPlansDefaultData: DefaultProduct[] = [
  {
    type: "subscription",
    name: "Yearly",
    caption: "$89.99 annually",
    stepTwoDescription: "Billed annually",
    productType: "yearly",
  },
  {
    type: "subscription",
    name: "Monthly",
    caption: "$120 annually",
    stepTwoDescription: "Billed monthly",
    productType: "monthly",
  },
]

const singlePurchasePlansDefaultData: DefaultProduct[] = [
  {
    type: "one-time-purchase",
    name: "Day Pass",
    caption: "Expires in 24 hours",
    stepTwoDescription: "Expires in 24 hours",
    productType: "daily",
  },
]

export type SubscriptionPlan = Omit<DefaultProduct, "priceId"> & {
  priceId: string
  priceDisplay?: string
  yearlySavings?: number | null
  stripeData: StripePriceWithProduct
}

export const useSubscriptionCheckoutModal = (entryPoint: CheckoutModalEntryPoint) => {
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([])
  const [singlePurchasePlans, setSinglePurchasePlans] = useState<SubscriptionPlan[]>([])
  const [step, setStep] = useState(1)
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [selectedProductType, setSelectedProductType] = useState<ProductType | null>(null)

  const { data: userEntitledToFreeTrialData } = useQuery(UserEntitledToFreeTrialDocument)
  const eligibleForFreeTrial = Boolean(
    userEntitledToFreeTrialData?.currentUser.creative?.proMembership?.eligibleForIntroductoryOffer
  )

  const fetchPriceData = async () => {
    const response = await fetch("/api/subscription/prices", { method: "GET" })
    const plans: StripeProductWithMetadata[] = await response.json()

    if (plans) {
      const subs = attachStripePriceDataToPlans(subscriptionPlansDefaultData, plans)
      const singles = attachStripePriceDataToPlans(singlePurchasePlansDefaultData, plans)
      setSubscriptionPlans(subs)
      setSinglePurchasePlans(singles)

      if (!selectedPlanId) {
        const yearly = subs.find(p => p.productType === "yearly")
        if (yearly) {
          setSelectedProductType(yearly.productType)
          setSelectedPlanId(yearly.priceId)
        }
      }
    }
  }

  useEffect(() => {
    fetchPriceData()
  }, [])

  const handleCheckoutClose = () => {
    // Use timeout so the user doesn't see the change
    if (step === 1) {
      ampli.planSelectionScreenExit({
        entry_point: entryPoint,
      })
    }
    setTimeout(() => {
      setStep(1)
      const defaultProduct = subscriptionPlans.find(p => p.productType === "yearly")
      if (defaultProduct) {
        setSelectedProductType(defaultProduct.productType)
        setSelectedPlanId(defaultProduct.priceId)
      }
    }, 500)
  }

  const onContinueWithPlan = async () => {
    if (!selectedPlanId) {
      toast.error("Please select a plan")
      return
    }

    const selectedPlan = [...subscriptionPlans, ...singlePurchasePlans].find(plan => plan.priceId === selectedPlanId)
    if (!selectedPlan) {
      toast.error("Could not find selected plan")
      return
    }
    ampli.purchaseSubscriptionStart({
      plan_length: selectedPlan?.productType,
      free_trial: eligibleForFreeTrial,
    })

    setStep(2)
  }

  const onSelectPlan = (priceId: string) => {
    const selectedPlan = [...subscriptionPlans, ...singlePurchasePlans].find(plan => plan.priceId === priceId)
    if (!selectedPlan) {
      toast.error("Could not find selected plan")
      return
    }
    setSelectedProductType(selectedPlan.productType)
    setSelectedPlanId(priceId)
    ampli.planSelect({
      plan_length: selectedPlan?.productType,
      free_trial: eligibleForFreeTrial,
    })
  }

  return {
    step,
    onChangeStep: setStep,
    handleCheckoutClose,
    selectedPlanId,
    onSelectPlan,
    subscriptionPlans,
    singlePurchasePlans,
    onContinueWithPlan,
    eligibleForFreeTrial,
    selectedProductType,
    setSelectedProductType,
  }
}
